import { ActionTree } from 'vuex';
import {
  EAddResourceAction,
  EAddResourceMutation,
  state as defaultState,
} from './add-resource.constants';
import { IAddResourceState } from './add-resource.types';
import { ResourceService } from '@/api/resources/services/resource.service';
import { EResourceType } from '@/api/resources/types/enum/resource-type';
import { IResource } from '@/api/resources/types/resource';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { IContentLinkBlock } from '@/content-link/types/content-link';
import { IVideo } from '@/types/Video';
import { IEditorBlock } from '@/editor/types';
import { IDocument } from '@lib-shared/types/Document';
import { EditorBlockService } from '@/editor/api/services/editor-block.service';
import { VideoService } from '@/api/video/services/video.service';
import { ContentLinkBlockService } from '@/api/content-link/services/content-link-block.service';
import { DocumentService } from '@/api/documents/services/document.service';
import { EnumDocumentType } from '@/enums/DocumentType';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IAddResourceState, RootState> = {
  async [EAddResourceAction.AddToNewDocument](
    { dispatch, commit },
    { resource, documentType }: { resource: IResource; documentType: EnumDocumentType }
  ) {
    commit(EAddResourceMutation.SetAdding, true);
    try {
      const documentId: string = await DocumentService.createDocument(documentType);
      dispatch(EAddResourceAction.AddToDocument, { resource, documentId });
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-create-document'),
        {
          root: true,
        }
      );
    }
  },
  async [EAddResourceAction.AddToDocument](
    { dispatch, commit },
    { resource, documentId }: { resource: IResource; documentId: string }
  ) {
    commit(EAddResourceMutation.SetAdding, true);
    commit(EAddResourceMutation.SetDocumentId, documentId);
    try {
      let addedResource: IResource;
      let copy: IContentLinkBlock | IEditorBlock | IVideo | IDocument;

      switch (resource.resourceType) {
        case EResourceType.ContentLink: {
          copy = await ContentLinkBlockService.copyContentLink(resource.contentId);
          await ResourceService.implementInDocument({
            documentId,
            resourceId: resource.id,
            contentId: copy.id,
          });
          addedResource = { ...resource, contentId: copy.id, block: copy };
          break;
        }

        case EResourceType.Template: {
          await ResourceService.implementInDocument({
            documentId,
            resourceId: resource.id,
            contentId: resource.contentId,
          });
          const block: Partial<IDocument> = await DocumentService.loadTemplateResourceDetails(
            resource.contentId
          );
          addedResource = { ...resource, block } as IResource;
          break;
        }

        case EResourceType.Editor: {
          copy = await EditorBlockService.copyEditorBlock(resource.contentId);
          await ResourceService.implementInDocument({
            documentId,
            resourceId: resource.id,
            contentId: copy.id,
          });
          addedResource = { ...resource, contentId: copy.id, block: copy };
          break;
        }

        case EResourceType.Video: {
          copy = await VideoService.copyVideo(resource.contentId);
          await ResourceService.implementInDocument({
            documentId,
            resourceId: resource.id,
            contentId: copy.id,
          });
          addedResource = { ...resource, contentId: copy.id, block: copy };
          break;
        }
      }

      dispatch(EAddResourceAction.AddSuccess, addedResource);
    } catch (error) {
      dispatch(EAddResourceAction.AddFailed);
    }
  },
  async [EAddResourceAction.AddToTemplate](
    { dispatch, commit },
    { resource, documentId }: { resource: IResource; documentId: string }
  ) {
    commit(EAddResourceMutation.SetAdding, true);
    try {
      await ResourceService.implementInTemplate({ documentId, resourceId: resource.id });
      dispatch(EAddResourceAction.AddSuccess, resource);
    } catch (error) {
      dispatch(EAddResourceAction.AddFailed);
    }
  },
  [EAddResourceAction.AddSuccess]({ commit }, resource: IResource) {
    commit(EAddResourceMutation.SetResource, resource);
    commit(EAddResourceMutation.SetAdding, false);
  },
  [EAddResourceAction.AddFailed]({ dispatch }) {
    dispatch(EAddResourceAction.Reset);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-add-resource'), {
      root: true,
    });
  },
  [EAddResourceAction.Reset]({ commit }) {
    commit(EAddResourceMutation.SetState, { ...defaultState });
  },
};
